/**
 * Parte D — "Aviso de privacidad para tus clientes" (Configuración › Usuarios,
 * acceso y legal). ÚNICO lugar donde el tenant obtiene y publica su aviso (TyC
 * v3.2 §9.3: "plantillas de aviso de privacidad según el caso" — una por
 * COMBINACIÓN de funciones; "para habilitar cada servicio que trate datos de tus
 * Clientes finales, deberás contar con un aviso de privacidad").
 *
 * Modelo (cerrado): el tenant publica UN solo aviso y DECLARA qué finalidades
 * cubre (3 casillas: facturación · reservas y pagos en línea · fidelidad). Cada
 * función exige su finalidad (assertAvisoPublished(finalidad)); despublicar o
 * republicar QUITANDO una finalidad con función activa se bloquea
 * (assertCanReduceCoverage). Las plantillas son AYUDA de descarga (7 combos):
 * documentos para COMPLETAR, nunca se publican. V210 (hotfix): se publica SOLO con
 * el documento propio del tenant (Word o PDF); /aviso/{slug} muestra ESE documento y el
 * panel ofrece la vista previa de lo que ve el público. Las casillas NUNCA se pre-marcan
 * solas: la atestación es del tenant (incluido el fundador, que re-atesta al publicar).
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { FileText, Download, AlertTriangle, Upload } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { api, API_BASE, getToken } from '../lib/api';
import { AvisoDocumentView } from '../cfdi/AvisoDocumentView';
import { avisoVersionLabel, templateForCovers, type AvisoDocumentKind } from '../cfdi/avisoDocument';
import { TENANT_TEMPLATES_V32, templateV32Path } from './legalConfig';

/** V168+V171 — estado del aviso (desacoplado de CFDI) + finalidades + archivo propio. */
interface NoticeLite {
  hosted: boolean;
  version: string | null;
  hostedAt: string | null;
  coversFacturacion: boolean;
  coversReservasPagos: boolean;
  coversFidelidad: boolean;
  fileName: string | null;
  fileUploadedAt: string | null;
  // V210
  fileMime: string | null;
  fileSha256: string | null;
  conversionError: string | null;
}

/** V210 — vista previa = lo mismo que ve el público. */
interface NoticePreview {
  fileName: string; fileUploadedAt: string | null; fileMime: string; fileSha256: string;
  documentHtml: string | null; conversionError: string | null;
}

const EMPTY: NoticeLite = {
  hosted: false, version: null, hostedAt: null,
  coversFacturacion: false, coversReservasPagos: false, coversFidelidad: false,
  fileName: null, fileUploadedAt: null, fileMime: null, fileSha256: null, conversionError: null,
};

const kindOf = (mime: string | null): AvisoDocumentKind =>
  mime === 'application/pdf' ? 'pdf'
    : mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ? 'docx' : 'other';

const FINALIDADES = [
  { key: 'facturacion' as const, label: 'Facturación (CFDI)', hint: 'Emites facturas a tus clientes con sus datos fiscales.' },
  { key: 'reservasPagos' as const, label: 'Reservas y pagos en línea', hint: 'Tienda en línea y/o citas: nombre, teléfono, correo, pagos.' },
  { key: 'fidelidad' as const, label: 'Fidelidad', hint: 'Programa de lealtad / monedero de tus clientes.' },
];

export function TenantPrivacyTemplateCard() {
  const { user } = useAuth() as any;
  const isSuper = user?.role === 'SUPER_USER';
  const slug: string = user?.tenant?.slug ?? '';
  const [notice, setNotice] = useState<NoticeLite | null>(null);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  // V210 — sin documento propio no se publica: el error ofrece la plantilla de SU combinación.
  const [needsFile, setNeedsFile] = useState(false);
  const [preview, setPreview] = useState<NoticePreview | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfSrc, setPdfSrc] = useState<string | null>(null);
  // Las 3 casillas de FINALIDAD (atestación del tenant; nunca pre-marcadas solas —
  // al cargar reflejan lo YA declarado, que sí es su declaración previa).
  const [covFact, setCovFact] = useState(false);
  const [covRes, setCovRes] = useState(false);
  const [covFid, setCovFid] = useState(false);
  // Ajuste smoke — FINALIDADES DISPONIBLES según las funciones del tenant: marcar
  // "Fidelidad" cuando el módulo no existe no significa nada, y las 7 plantillas se
  // filtran a las que combinan SUS funciones. reservas_pagos: todas las verticales
  // tienen tienda o reservas → siempre disponible. facturación: solo con CFDI.
  // fidelidad (V205): disponible donde existe el programa = verticales con POS
  // (gastro/boutique/gym); el backend lo gatea por módulo → si /loyalty responde, aplica.
  const [cfdiEnabled, setCfdiEnabled] = useState(false);
  const [loyaltyAvailable, setLoyaltyAvailable] = useState(false);
  const available = { facturacion: cfdiEnabled, reservasPagos: true, fidelidad: loyaltyAvailable };

  const load = () =>
    api<NoticeLite>('/me/privacy-notice')
      .then((n) => {
        setNotice(n);
        setCovFact(n.coversFacturacion); setCovRes(n.coversReservasPagos); setCovFid(n.coversFidelidad);
      })
      .catch(() => setNotice(EMPTY));
  useEffect(() => {
    if (!isSuper) { setNotice(EMPTY); return; }
    load();
    // Secuencial tras el estado del aviso (sin Promise.all ni carreras de UI).
    api<{ enabled: boolean }>('/me/cfdi-issuer')
      .then((s) => setCfdiEnabled(!!s?.enabled)).catch(() => setCfdiEnabled(false))
      .then(() => api('/loyalty/pos/status'))
      .then(() => setLoyaltyAvailable(true)).catch(() => setLoyaltyAvailable(false));
  }, [isSuper]);

  const errMsg = (e: any): string => {
    const m = e?.body?.message;
    return (Array.isArray(m) ? m.join(' ') : m) || e?.message || 'No se pudo completar la operación.';
  };
  const run = async (fn: () => Promise<void>, okMsg?: string) => {
    setBusy(true); setErr(null); setOk(null); setNeedsFile(false);
    try { await fn(); await load(); setPreview(null); if (okMsg) setOk(okMsg); }
    catch (e) { setErr(errMsg(e)); setNeedsFile((e as any)?.body?.code === 'aviso_file_required'); }
    finally { setBusy(false); }
  };

  // V210 — vista previa del documento (antes y después de publicar).
  const fileSha = notice?.fileSha256 ?? null;
  useEffect(() => {
    if (!showPreview || !fileSha) return;
    let url: string | null = null;
    api<NoticePreview>('/me/privacy-notice/preview').then(async (p) => {
      setPreview(p);
      if (kindOf(p.fileMime) === 'pdf') {
        const res = await fetch(`${API_BASE}/me/privacy-notice/file`, { headers: { Authorization: `Bearer ${getToken() ?? ''}` } });
        if (res.ok) { url = URL.createObjectURL(await res.blob()); setPdfSrc(url); }
      } else setPdfSrc(null);
    }).catch(() => setPreview(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [showPreview, fileSha]);

  const downloadOwn = async () => {
    const res = await fetch(`${API_BASE}/me/privacy-notice/file`, { headers: { Authorization: `Bearer ${getToken() ?? ''}` } });
    if (!res.ok) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(await res.blob());
    a.download = notice?.fileName ?? 'aviso';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const hosted = !!notice?.hosted;
  const url = `${window.location.origin}/aviso/${slug}`;

  useEffect(() => {
    if (hosted && slug) QRCode.toDataURL(url, { margin: 1, width: 160 }).then(setQr).catch(() => setQr(null));
    else setQr(null);
  }, [hosted, url, slug]);

  if (!isSuper || !notice || !slug) return null;

  const publish = () => run(async () => {
    await api('/me/privacy-notice/hosted', {
      method: 'POST',
      body: {
        hosted: true,
        // V210 — la versión ya no la manda el cliente: la fija la DB por el documento.
        // Hallazgo #3 (smoke 08/09): casilla OCULTA (finalidad no disponible en la
        // vertical) = FALSE EXPLÍCITO — nunca se re-manda un valor viejo que el tenant
        // ya no puede ver ni apagar (así quedó atorado covers_fidelidad en Boutique).
        coversFacturacion: covFact && available.facturacion,
        coversReservasPagos: covRes && available.reservasPagos,
        coversFidelidad: covFid && available.fidelidad,
      },
    });
  }, hosted ? 'Aviso republicado con las finalidades declaradas.' : 'Aviso publicado.');

  const upload = (file: File) => run(async () => {
    const fd = new FormData();
    fd.append('file', file);
    // fetch directo: api() serializa JSON; multipart necesita el FormData crudo.
    const res = await fetch(`${API_BASE}/me/privacy-notice/file`, {
      method: 'POST', body: fd,
      headers: { Authorization: `Bearer ${getToken() ?? ''}` },
    });
    if (!res.ok) throw { body: await res.json().catch(() => null) };
  }, 'Documento cargado. Revisa la vista previa: es lo que verán tus clientes.');

  const inputCls = 'w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-600';
  const chip = (on: boolean, label: string) => (
    <span key={label} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${on ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400 line-through'}`}>{label}</span>
  );

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white p-6 md:p-8">
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-900 text-white">
          <FileText className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-lg tracking-tight text-slate-900">Aviso de privacidad para tus clientes</h3>
          <p className="mt-1 text-sm text-slate-500">
            Publicas <strong>un solo aviso</strong> y declaras qué finalidades cubre. Cada función que recibe datos de
            tus clientes (tienda, reservas, facturación, fidelidad) exige su finalidad declarada para encenderse.
          </p>

          {/* Plantillas v3.2 — una por COMBINACIÓN de funciones (ayuda de descarga). */}
          <div className="mt-4 rounded-xl border border-slate-200 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Plantillas por caso (v3.2)</p>
            <p className="mt-1 text-[11px] text-slate-500">
              Elige la que <strong>combine tus funciones</strong>, complétala con tus datos y cárgala abajo — o publícala por tu cuenta.
            </p>
            <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50/70 px-3 py-2 text-[11px] text-amber-900 ring-1 ring-inset ring-amber-200">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <span>Son una <strong>recomendación, no un documento legal listo</strong>: sustituye los campos <code className="font-mono">[...]</code> y valídala con tu abogado.</span>
            </div>
            <ul className="mt-2 divide-y divide-slate-100">
              {/* Solo las plantillas que combinan las funciones ACTIVAS del tenant. */}
              {TENANT_TEMPLATES_V32
                .filter((t) => (!t.covers.facturacion || available.facturacion)
                  && (!t.covers.reservasPagos || available.reservasPagos)
                  && (!t.covers.fidelidad || available.fidelidad))
                .map((t) => (
                <li key={t.file} className="flex items-center justify-between gap-2 py-1.5">
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{t.title}</span>
                  <a href={templateV32Path(t.file, 'docx', t.version)} download
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50">
                    <Download className="h-3.5 w-3.5" /> Word
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Documento PROPIO — requisito para publicar (V210): es lo ÚNICO que ve el público. */}
          <div className="mt-4 rounded-xl border border-slate-200 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tu documento</p>
            {notice.fileName ? (
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-700">
                <span className="truncate font-medium">{notice.fileName}</span>
                <span className="text-[11px] text-slate-400">
                  {notice.fileUploadedAt ? `cargado ${new Date(notice.fileUploadedAt).toLocaleDateString('es-MX')}` : ''}
                </span>
                {!hosted && (
                  <button type="button" disabled={busy}
                    onClick={() => run(async () => { await api('/me/privacy-notice/file', { method: 'DELETE' }); }, 'Documento quitado.')}
                    className="text-[11px] text-slate-500 underline hover:text-rose-600 disabled:opacity-50">Quitar</button>
                )}
              </div>
            ) : (
              <p className="mt-1 text-[11px] text-slate-500">Carga tu aviso en Word (.docx) o PDF, máx. 5 MB. <strong>Es lo que verán tus clientes</strong>; sin él no se puede publicar.</p>
            )}
            <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
              <Upload className="h-3.5 w-3.5" /> {notice.fileName ? 'Reemplazar archivo' : 'Cargar mi aviso'}
              <input type="file" className="hidden" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
            </label>
            {notice.conversionError && (
              <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-[11px] text-rose-700 ring-1 ring-inset ring-rose-200">
                No pudimos mostrar tu Word como texto en la página ({notice.conversionError}). Tus clientes verán solo el botón
                para descargarlo. Prueba guardarlo de nuevo como .docx o cárgalo en PDF.
              </p>
            )}
            {notice.fileName && (
              <button type="button" onClick={() => setShowPreview((v) => !v)}
                className="mt-2 ml-2 inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
                {showPreview ? 'Ocultar vista previa' : 'Ver cómo lo verán tus clientes'}
              </button>
            )}
            {showPreview && preview && (
              <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Vista previa {hosted ? '— así está publicado' : '— aún NO está publicado'}
                </p>
                <p className="mt-1 text-[11px] text-slate-400">{avisoVersionLabel(preview.fileName, preview.fileUploadedAt, preview.fileSha256)}</p>
                <div className="mt-3 max-h-[60vh] overflow-y-auto">
                  <AvisoDocumentView kind={kindOf(preview.fileMime)} html={preview.documentHtml} pdfSrc={pdfSrc}
                    fileName={preview.fileName} onDownload={downloadOwn} />
                </div>
              </div>
            )}
          </div>

          {/* Finalidades (atestación) + publicar/republicar. */}
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">¿Qué cubre tu aviso?</p>
            <p className="mt-1 text-[11px] text-slate-500">Marca solo lo que tu aviso publicado realmente declara. Es tu atestación.</p>
            <div className="mt-2 space-y-1.5">
              {/* Solo las finalidades que el tenant PUEDE activar (funciones existentes). */}
              {FINALIDADES.filter((f) => available[f.key]).map((f) => {
                const val = f.key === 'facturacion' ? covFact : f.key === 'reservasPagos' ? covRes : covFid;
                const set = f.key === 'facturacion' ? setCovFact : f.key === 'reservasPagos' ? setCovRes : setCovFid;
                return (
                  <label key={f.key} className="flex items-start gap-2 text-sm text-slate-700">
                    <input type="checkbox" className="mt-0.5" checked={val} onChange={(e) => set(e.target.checked)} />
                    <span><span className="font-medium">{f.label}</span> <span className="block text-[11px] text-slate-500">{f.hint}</span></span>
                  </label>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              {hosted ? (
                <p className="text-[11px] text-emerald-700">
                  ✓ Publicado {notice.hostedAt ? `desde ${new Date(notice.hostedAt).toLocaleDateString('es-MX')}` : ''}
                  {notice.fileName && notice.fileSha256 ? ` · ${avisoVersionLabel(notice.fileName, notice.fileUploadedAt, notice.fileSha256)}` : ''}
                </p>
              ) : <span />}
              <button type="button" onClick={publish} disabled={busy}
                className="rounded-lg bg-[var(--brand-primary)] px-4 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                {busy ? 'Guardando…' : hosted ? 'Republicar con estas finalidades' : 'Publicar mi aviso con FluidSol'}
              </button>
            </div>
          </div>

          {/* Estado publicado: link + QR + finalidades declaradas + despublicar. */}
          {hosted && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-medium text-emerald-800">Tu aviso público cubre:</span>
                {chip(notice.coversFacturacion, 'Facturación')}
                {chip(notice.coversReservasPagos, 'Reservas y pagos')}
                {chip(notice.coversFidelidad, 'Fidelidad')}
              </div>
              <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex flex-1 items-center gap-2">
                  <input readOnly value={url} onFocus={(e) => e.target.select()} className={inputCls} />
                  <button type="button"
                    onClick={() => { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className="shrink-0 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-600 hover:bg-slate-100">
                    {copied ? 'Copiado ✓' : 'Copiar'}
                  </button>
                  <a href={url} target="_blank" rel="noreferrer"
                    className="shrink-0 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-600 hover:bg-slate-100">
                    Ver
                  </a>
                </div>
                {qr && <img src={qr} alt="QR del aviso" className="h-20 w-20 rounded-lg border border-slate-100 bg-white" />}
              </div>
              <button type="button"
                onClick={() => run(async () => { await api('/me/privacy-notice/hosted', { method: 'POST', body: { hosted: false, coversFacturacion: false, coversReservasPagos: false, coversFidelidad: false } }); }, 'Aviso despublicado.')}
                disabled={busy}
                className="mt-2 text-[11px] text-slate-500 underline hover:text-slate-700 disabled:opacity-50">
                Despublicar
              </button>
            </div>
          )}

          {/* Mensajes del gate (409 accionable: "Desactiva primero…" / finalidad en uso) y éxito. */}
          {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
          {needsFile && (() => {
            const t = templateForCovers({
              facturacion: covFact && available.facturacion,
              reservasPagos: covRes && available.reservasPagos,
              fidelidad: covFid && available.fidelidad,
            });
            return t ? (
              <a href={t.href} download
                className="mt-2 inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                <Download className="h-3.5 w-3.5" /> Plantilla para completar: {t.title} ({t.templateVersion}, Word)
              </a>
            ) : null;
          })()}
          {ok && <p className="mt-3 text-sm text-emerald-600">{ok}</p>}
        </div>
      </div>
    </div>
  );
}

export default TenantPrivacyTemplateCard;
